import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Edit2, Trash2, X, Check, ChevronRight, ChevronDown, RefreshCw } from 'lucide-react';
import { usersAPI, shiftHistoryAPI } from '../../services/api';

const EMPTY_FORM = { shift_id: '', start_date: '', end_date: '', remark: '' };

const ShiftManagement = () => {
  const [usersWithHistory, setUsersWithHistory] = useState([]);
  const [loadingList, setLoadingList]           = useState(true);

  const [search, setSearch]             = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const [selectedUser, setSelectedUser] = useState(null);
  const [history, setHistory]           = useState([]);
  const [shifts, setShifts]             = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [error, setError]         = useState('');
  const [saving, setSaving]       = useState(false);
  const [showShiftRef, setShowShiftRef] = useState(false);

  const loadUsersWithHistory = useCallback(() => {
    setLoadingList(true);
    shiftHistoryAPI.getUsersWithHistory()
      .then(res => setUsersWithHistory(res.data.users || []))
      .catch(() => setUsersWithHistory([]))
      .finally(() => setLoadingList(false));
  }, []);

  useEffect(() => {
    usersAPI.getShifts()
      .then(res => setShifts(res.data.shifts || []))
      .catch(() => {});
    loadUsersWithHistory();
  }, [loadUsersWithHistory]);

  // Debounced search
  useEffect(() => {
    if (!search.trim()) { setSearchResults([]); return; }
    const timer = setTimeout(() => {
      usersAPI.getUsers({ search: search.trim(), per_page: 20 })
        .then(res => setSearchResults(res.data.users || []))
        .catch(() => setSearchResults([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const loadHistory = useCallback((user) => {
    shiftHistoryAPI.getUserHistory(user.id)
      .then(res => setHistory(res.data.history || []))
      .catch(() => setHistory([]));
  }, []);

  const selectUser = (user) => {
    setSelectedUser(user);
    setSearch('');
    setSearchResults([]);
    loadHistory(user);
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      shift_id:   String(row.shift_id),
      start_date: row.start_date || '',
      end_date:   row.end_date   || '',
      remark:     row.remark     || '',
    });
    setError('');
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingId(null); setForm(EMPTY_FORM); };

  const handleSave = async () => {
    if (!form.shift_id || !form.start_date) {
      setError('Shift and start date are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        shift_id:   parseInt(form.shift_id),
        start_date: form.start_date,
        end_date:   form.end_date || null,
        remark:     form.remark   || null,
      };
      if (editingId) {
        await shiftHistoryAPI.updateEntry(editingId, payload);
      } else {
        await shiftHistoryAPI.addEntry(selectedUser.id, payload);
      }
      closeModal();
      loadHistory(selectedUser);
      loadUsersWithHistory();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (historyId) => {
    if (!window.confirm('Delete this shift history entry?')) return;
    try {
      await shiftHistoryAPI.deleteEntry(historyId);
      loadHistory(selectedUser);
      loadUsersWithHistory();
    } catch (e) {
      alert(e.response?.data?.error || 'Delete failed.');
    }
  };

  const fmtTime = (t) => t ? t.substring(0, 5) : '—';

  return (
    <div className="space-y-4">

      {/* Shift reference — collapsible */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <button
          onClick={() => setShowShiftRef(v => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <span>Shift Reference</span>
          {showShiftRef ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
        </button>
        {showShiftRef && (
          <div className="border-t border-gray-100 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Code', 'Name', 'Start', 'End'].map(h => (
                    <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {shifts.map(s => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-gray-700">{s.code}</td>
                    <td className="px-4 py-2 text-gray-900">{s.name}</td>
                    <td className="px-4 py-2 text-gray-700">{fmtTime(s.start_time)}</td>
                    <td className="px-4 py-2 text-gray-700">{fmtTime(s.end_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    <div className="flex gap-6 min-h-0">
      {/* ── Left panel: users with history ── */}
      <div className="w-72 flex-shrink-0 bg-white rounded-lg shadow flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800 text-sm">Users with Shift History</h3>
          <button
            onClick={loadUsersWithHistory}
            className="p-1 text-gray-400 hover:text-blue-600 rounded"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        {/* Search to add a new user */}
        <div className="px-3 py-2 border-b border-gray-100 relative">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input
            className="pl-7 pr-3 py-1.5 w-full border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Find user to add…"
            value={search}
            onChange={e => { setSearch(e.target.value); }}
          />
          {searchResults.length > 0 && (
            <ul className="absolute z-10 left-3 right-3 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto text-xs">
              {searchResults.map(u => (
                <li
                  key={u.id}
                  className="flex items-center justify-between px-3 py-2 hover:bg-blue-50 cursor-pointer"
                  onClick={() => selectUser(u)}
                >
                  <span className="font-medium text-gray-800 truncate">{u.name}</span>
                  <ChevronRight className="h-3 w-3 text-gray-400 flex-shrink-0" />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* User list */}
        <div className="flex-1 overflow-y-auto">
          {loadingList ? (
            <div className="px-4 py-6 text-center text-xs text-gray-400">Loading…</div>
          ) : usersWithHistory.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-gray-400">No shift history yet.</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {usersWithHistory.map(u => (
                <li
                  key={u.id}
                  onClick={() => selectUser(u)}
                  className={`px-4 py-3 cursor-pointer hover:bg-blue-50 transition-colors ${
                    selectedUser?.id === u.id ? 'bg-blue-50 border-l-2 border-blue-500' : ''
                  }`}
                >
                  <p className="text-sm font-medium text-gray-800 truncate">{u.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{u.staff_no} &bull; {u.shift_name || '—'}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* ── Right panel: history detail ── */}
      <div className="flex-1 min-w-0">
        {!selectedUser ? (
          <div className="bg-white rounded-lg shadow flex items-center justify-center h-64 text-gray-400 text-sm">
            Select a user to view their shift history.
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="font-semibold text-gray-900">{selectedUser.name}</h3>
                <p className="text-sm text-gray-500">
                  Staff No: {selectedUser.staff_no} &bull; Current shift: {selectedUser.shift_name || '—'}
                </p>
              </div>
              <button
                onClick={openAdd}
                className="flex items-center gap-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" /> Add Entry
              </button>
            </div>

            {history.length === 0 ? (
              <div className="px-6 py-10 text-center text-gray-400 text-sm">
                No shift history recorded for this user.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Shift', 'Start Date', 'End Date', 'Status', 'Updated By', 'Remark', ''].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {history.map(row => (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {row.shift_code && <span className="mr-1 text-blue-600">[{row.shift_code}]</span>}
                          {row.shift_name || row.shift_id}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{row.start_date}</td>
                        <td className="px-4 py-3 text-gray-700">{row.end_date || '—'}</td>
                        <td className="px-4 py-3">
                          {row.end_date === null ? (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Current</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">Closed</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-500">{row.updated_by_name || '—'}</td>
                        <td className="px-4 py-3 text-gray-500 max-w-xs truncate">{row.remark || '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => openEdit(row)}
                              className="p-1 text-gray-400 hover:text-blue-600 rounded"
                              title="Edit"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(row.id)}
                              className="p-1 text-gray-400 hover:text-red-600 rounded"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Add / Edit Modal ── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 text-base">
                {editingId ? 'Edit Shift History Entry' : `Add Entry — ${selectedUser?.name}`}
              </h3>
              <button onClick={closeModal} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            {error && (
              <div className="mb-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Shift *</label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={form.shift_id}
                  onChange={e => setForm(f => ({ ...f, shift_id: e.target.value }))}
                >
                  <option value="">— Select shift —</option>
                  {shifts.map(s => (
                    <option key={s.id} value={s.id}>[{s.code}] {s.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Start Date *</label>
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.start_date}
                    onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    End Date <span className="text-gray-400">(blank = current)</span>
                  </label>
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.end_date}
                    onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Remark</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Optional note"
                  value={form.remark}
                  onChange={e => setForm(f => ({ ...f, remark: e.target.value }))}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Check className="h-4 w-4" />
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default ShiftManagement;
